import type { OperationsStampDto } from "@/api/endpoints";
import { fmtET } from "@/lib/format";
import type { OperationsState } from "../operationsSchema";
import { lensLabel, workTypeLabel } from "../operationsLabels";

interface Props {
  state: OperationsState;
  stamp: OperationsStampDto;
  /** Shown as a link at the end of the sentence when any slicer or the lens is set. */
  onClear?: () => void;
  hasFilters?: boolean;
  /** The stamp belongs to the previous filters while the new ones load: say so instead of the snapshot time. */
  updating?: boolean;
}

function requestTypeSentence(state: OperationsState): string {
  const parts = [
    ...(state.onboardingsOnly ? ["Onboarding journeys"] : []),
    ...(state.redeployment ? ["Redeployment"] : []),
    ...state.requestType.map(workTypeLabel),
  ];
  return parts.length ? parts.join(" + ") : "All request types";
}

/** "prefix A, B" for a filtered control, the fallback when it is not filtered. */
function listOr(values: string[], fallback: string, prefix = ""): string {
  return values.length ? `${prefix}${values.join(", ")}` : fallback;
}

/** Breadcrumb sentence (what the screen is filtered to) + the snapshot stamp (what the data is). */
export function StampLine({ state, stamp, onClear, hasFilters, updating }: Props) {
  const parts = [
    listOr(state.division, "All divisions", "Division "),
    listOr(state.manager, "All managers", "Manager "),
    listOr(state.team, "All teams", "Team "),
    listOr(state.assigned, "All owners", "Owner "),
    listOr(state.client, "All clients"),
    listOr(state.lob, "All lines of business"),
    listOr(state.eng, "All engagement types", "Engagement "),
    requestTypeSentence(state),
    listOr(state.country, "All countries"),
    state.period === "today" ? "Period today" : "Period week to date",
  ];
  const window = stamp.coverage.days
    ? `open items created in the last ${stamp.coverage.days} days`
    : "all open items";
  return (
    <div className="ops-stamp" role="note">
      <span className="ops-stamp-sentence">
        {parts.join(" · ")}
        {(state.lens || state.pen) && <> · <b className={`ops-lens-word ${state.lens ?? state.pen}`}>{lensLabel(state.lens, state.pen)} only</b></>}
        {hasFilters && onClear && (
          <>
            {" · "}
            <button type="button" className="ops-clear-link" onClick={onClear}>
              <span className="material-symbols-rounded" aria-hidden="true">filter_alt_off</span>
              Clear filters
            </button>
          </>
        )}
      </span>
      {updating ? (
        <span className="ops-stamp-right ops-stamp-updating" role="status">
          <span className="material-symbols-rounded" aria-hidden="true">progress_activity</span>
          Updating…
        </span>
      ) : (
        <span className="ops-stamp-right">
          <span className="material-symbols-rounded" aria-hidden="true">schedule</span>
          Snapshot {fmtET(stamp.loadedAtUtc)} · {window}
          {!stamp.activity.available && (
            <span className="ops-stamp-warn" title="Task data was unavailable for this snapshot; attention shows as Unknown.">
              · activity unavailable
            </span>
          )}
        </span>
      )}
    </div>
  );
}
